import type { Contractor } from '@/services/contractorService'
import { resolveStorageUrl } from '@/utils/api'

const CLIENT_PORTAL_LOGO_STORAGE_KEY = 'client_portal_logo_url'

export type ClientPortalFeatureFlags = {
  vehicleFeaturesEnabled: boolean
  bikeFeaturesEnabled: boolean
}

function metadataRecordFromUnknown(raw: unknown): Record<string, unknown> | null {
  if (raw == null) return null
  let value = raw
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value)
    } catch {
      return null
    }
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>
  return null
}

function metadataRecord(c: Contractor | null | undefined): Record<string, unknown> | null {
  return metadataRecordFromUnknown(c?.metadata)
}

/** Merge admin/client metadata onto an impersonation session contractor (impersonate often omits metadata). */
export function mergeContractorMetadata(
  contractor: Contractor | null | undefined,
  metadata: unknown,
): Contractor | null | undefined {
  if (!contractor) return contractor
  const overlay = metadataRecordFromUnknown(metadata)
  if (!overlay) return contractor
  const existing = metadataRecord(contractor) || {}
  return { ...contractor, metadata: { ...overlay, ...existing } }
}

export function persistClientLogoUrl(url: string): void {
  if (typeof window === 'undefined' || !url.trim()) return
  localStorage.setItem(CLIENT_PORTAL_LOGO_STORAGE_KEY, url)
}

/** Resolve client logo URL from profile metadata, with optional localStorage fallback. */
export function getClientLogoUrlFromContractor(
  c: Contractor | null | undefined,
  options?: { fallbackToLocalStorage?: boolean; localStorageKey?: string },
): string {
  const md = metadataRecord(c)
  const record = c as Record<string, unknown> | null | undefined
  const candidate = md?.logo_url ?? md?.logoUrl ?? md?.logo_path ?? md?.logoPath ?? record?.logo_url
  if (typeof candidate === 'string' && candidate.trim()) {
    const resolved = resolveStorageUrl(candidate)
    if (resolved && options?.fallbackToLocalStorage !== false) {
      persistClientLogoUrl(resolved)
    }
    return resolved
  }

  if (options?.fallbackToLocalStorage === false || typeof window === 'undefined') {
    return ''
  }

  const storageKey = options?.localStorageKey ?? CLIENT_PORTAL_LOGO_STORAGE_KEY
  const stored = localStorage.getItem(storageKey)
  if (stored?.trim()) return resolveStorageUrl(stored)
  return ''
}

/** Resolve and cache the client logo URL on the auth store / layout. */
export function syncClientLogoUrl(contractor: Contractor | null | undefined): string {
  return getClientLogoUrlFromContractor(contractor, { fallbackToLocalStorage: true })
}

/**
 * Vehicle and bike portal features are always on.
 * Per-client toggles were removed; keep this helper so portal/layout call sites stay stable.
 */
export function getClientPortalFeatureFlags(_c?: Contractor | null): ClientPortalFeatureFlags {
  return {
    vehicleFeaturesEnabled: true,
    bikeFeaturesEnabled: true,
  }
}
